/**
 * O crédito corrigido pelo painel (E203.F4.C, F-E203.F4-06): o ALVO é a leitura que a correção muda
 * (o arquivo e a página do crédito, que formam a chave `<documento>:<pagina>:<campo>`); a FONTE é
 * de onde o perito tirou o valor, escolhida à parte. Com mais de um crédito lido, a pessoa escolhe o
 * alvo (arquivo, página, contrato e valor de cada um), sem nada pré-escolhido.
 */
import { Field, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { nomeDoDocumento } from '@/lib/exame_eletronico_peca';
import type { CreditoNoPainelData } from '@/lib/revisaoDoDado';

import type { Mesa } from './tipos';

const t = ptBR.painelDoDado.credito;

/** O nome do arquivo do crédito, pelas peças da mesa; sem a peça, "Comprovante". */
export function arquivoDoCredito(mesa: Mesa, documentoId: string): string {
  const peca = (mesa.ccbs ?? []).find((p) => p.source_document_id === documentoId);
  if (peca === undefined) return t.comprovante;
  return nomeDoDocumento(peca) ?? peca.original_filename;
}

export function rotuloDoCredito(mesa: Mesa, credito: CreditoNoPainelData): string {
  return t.opcaoDoAlvo
    .replace('{arquivo}', arquivoDoCredito(mesa, credito.documento_id))
    .replace('{pagina}', String(credito.pagina))
    .replace('{contrato}', credito.contrato ?? t.semLeitura)
    .replace('{valor}', credito.valor ?? t.semLeitura);
}

export function AlvoDoCredito({
  creditos,
  mesa,
  escolhido,
  onEscolher,
}: {
  creditos: readonly CreditoNoPainelData[];
  mesa: Mesa;
  /** A posição do crédito escolhido na lista; `''` enquanto nenhum foi escolhido. */
  escolhido: string;
  onEscolher: (posicao: string) => void;
}): JSX.Element {
  return (
    <Field label={t.alvo} htmlFor="painel-credito-alvo">
      <Select
        id="painel-credito-alvo"
        value={escolhido}
        onChange={(e) => onEscolher(e.target.value)}
        data-testid="painel-credito-alvo"
      >
        <option value="" disabled>
          {t.escolhaOAlvo}
        </option>
        {creditos.map((c, i) => (
          <option key={`${c.documento_id}:${c.pagina}:${i}`} value={String(i)}>
            {rotuloDoCredito(mesa, c)}
          </option>
        ))}
      </Select>
    </Field>
  );
}
